import { Button } from '../ui/button';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';

export const PaginationControls = ({ pagination, onPageChange, itemsPerPage, onItemsPerPageChange, language = 'es' }) => {
  const t = language === 'es' ? {
    showing: 'Mostrando',
    perPage: 'por página'
  } : {
    showing: 'Showing',
    perPage: 'per page'
  };

  if (!pagination || pagination.total_pages <= 1) {
    // Still show items per page selector even with 1 page
    if (onItemsPerPageChange) {
      return (
        <div className="flex items-center justify-center gap-2 mt-8">
          <span className="text-sm text-muted-foreground">{t.showing}</span>
          <select 
            value={itemsPerPage || 50}
            onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
            className="border border-border rounded-md px-2 py-1 text-sm bg-background"
          >
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={200}>200</option>
            <option value={500}>500</option>
          </select>
          <span className="text-sm text-muted-foreground">{t.perPage}</span>
        </div>
      );
    }
    return null;
  }

  const { page: currentPage, total_pages: totalPages, has_prev, has_next } = pagination;

  // Generate page numbers with ellipsis
  const getPageNumbers = () => {
    const pages = [];
    
    // Always show first page
    if (currentPage > 3) {
      pages.push(1);
      if (currentPage > 4) pages.push('...');
    }
    
    // Show pages around current
    for (let i = Math.max(1, currentPage - 2); i <= Math.min(totalPages, currentPage + 2); i++) {
      pages.push(i);
    }
    
    // Always show last page
    if (currentPage < totalPages - 2) {
      if (currentPage < totalPages - 3) pages.push('...');
      pages.push(totalPages);
    }
    
    return pages;
  };

  return (
    <div className="flex items-center justify-center gap-4 mt-8 flex-wrap" data-testid="pagination-controls">
      {/* Items per page selector */}
      {onItemsPerPageChange && (
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">{t.showing}</span>
          <select 
            value={itemsPerPage || 50}
            onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
            className="border border-border rounded-md px-2 py-1 text-sm bg-background"
          >
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={200}>200</option>
            <option value={500}>500</option>
          </select>
          <span className="text-sm text-muted-foreground">{t.perPage}</span>
        </div>
      )}

      {/* First page button */}
      <Button
        variant="outline"
        size="sm"
        onClick={() => onPageChange(1)}
        disabled={currentPage === 1}
        className="hidden sm:flex"
        data-testid="pagination-first"
      >
        <ChevronsLeft className="h-4 w-4" />
      </Button>
      
      {/* Previous page button */}
      <Button
        variant="outline"
        size="sm"
        onClick={() => onPageChange(currentPage - 1)}
        disabled={!has_prev}
        data-testid="pagination-prev"
      >
        <ChevronLeft className="h-4 w-4" />
      </Button>
      
      {/* Page numbers */}
      {getPageNumbers().map((p, idx) => (
        p === '...' ? (
          <span key={`ellipsis-${idx}`} className="px-2 text-muted-foreground">...</span>
        ) : (
          <Button
            key={p}
            variant={p === currentPage ? "default" : "outline"}
            size="sm"
            onClick={() => onPageChange(p)}
            className="min-w-[36px]"
            data-testid={`pagination-page-${p}`}
          >
            {p}
          </Button>
        )
      ))}
      
      {/* Next page button */}
      <Button
        variant="outline"
        size="sm"
        onClick={() => onPageChange(currentPage + 1)}
        disabled={!has_next}
        data-testid="pagination-next"
      >
        <ChevronRight className="h-4 w-4" />
      </Button>
      
      {/* Last page button */}
      <Button
        variant="outline"
        size="sm"
        onClick={() => onPageChange(totalPages)}
        disabled={currentPage === totalPages}
        className="hidden sm:flex"
        data-testid="pagination-last"
      >
        <ChevronsRight className="h-4 w-4" />
      </Button>
    </div>
  );
};

export default PaginationControls;
